/**
 * ActivityIcon — renders a branded Lucide icon for timeline cards.
 *
 * Takes an emoji string (from getActivityEmoji/getMealEmoji or LLM output)
 * and renders the corresponding Lucide icon with brand-consistent styling.
 * Falls back to the original emoji if no mapping exists.
 *
 * Usage:
 *   <ActivityIcon emoji="🍣" size={20} />
 *   <ActivityIcon emoji={icon} size={24} className="flex-shrink-0" />
 */

import React from 'react';
import { getIconForEmoji } from '@wander/shared/utils/iconMap.js';
import {
  Landmark, Palette, Church, Camera, Building2, Mountain, Waves, TreePine,
  Droplets, Sunrise, Star, Fish, Snowflake, Glasses, Ship, Bike, Zap, Wind,
  Flag, Music, Flower2, ShoppingBag, ShoppingCart, Wine, Beer, Sailboat,
  Plane, PlaneLanding, CloudSun, ChefHat, Footprints, BellRing, Luggage,
  TrainFront, Car, Bus, Hotel, Sparkles, UtensilsCrossed, Soup, Flame,
  Sandwich, Croissant, Leaf, Beef, Circle, Salad, Cake, Egg, Coffee,
  Martini, Utensils, Umbrella, PawPrint, Castle, Building, Drama,
  CupSoda, MountainSnow, Dice5, TrainFrontTunnel, IceCream, ToyBrick,
  MapPin, Target, Pizza, Drumstick, Cpu, Eye,
} from 'lucide-react';

// Map Lucide component names to actual components
const ICON_COMPONENTS = {
  Landmark, Palette, Church, Camera, Building2, Mountain, Waves, TreePine,
  Droplets, Sunrise, Star, Fish, Snowflake, Glasses, Ship, Bike, Zap, Wind,
  Flag, Music, Flower2, ShoppingBag, ShoppingCart, Wine, Beer, Sailboat,
  Plane, PlaneLanding, CloudSun, ChefHat, Footprints, BellRing, Luggage,
  TrainFront, Car, Bus, Hotel, Sparkles, UtensilsCrossed, Soup, Flame,
  Sandwich, Croissant, Leaf, Beef, Circle, Salad, Cake, Egg, Coffee,
  Martini, Utensils, Umbrella, PawPrint, Castle, Building, Drama,
  CupSoda, MountainSnow, Dice5, TrainFrontTunnel, IceCream, ToyBrick,
  MapPin, Target, Pizza, Drumstick, Cpu, Eye,
};

export default function ActivityIcon({ emoji, size = 20, className = '', style = {} }) {
  if (!emoji) return null;

  const mapping = getIconForEmoji(emoji);

  if (!mapping) {
    // No mapping — render original emoji as text
    return (
      <span className={className} style={{ fontSize: size, lineHeight: 1, ...style }}>
        {emoji}
      </span>
    );
  }

  const IconComponent = ICON_COMPONENTS[mapping.icon];
  if (!IconComponent) {
    // Icon name in mapping but component not imported — fall back to emoji
    return (
      <span className={className} style={{ fontSize: size, lineHeight: 1, ...style }}>
        {emoji}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-lg ${className}`}
      style={{
        width: size + 8,
        height: size + 8,
        backgroundColor: `${mapping.color}14`,
        ...style,
      }}
    >
      <IconComponent
        size={size}
        color={mapping.color}
        strokeWidth={1.75}
      />
    </span>
  );
}
